// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Calendar, MapPin, User, Mail, Phone, Building, Users, DollarSign, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { parseLocalDate } from '@/utils/formatters';

interface RegistrationData {
  id: string;
  coach_name: string;
  coach_email: string;
  coach_phone: string;
  college_name: string;
  division: string;
  payment_status: string;
  metadata: {
    filler_name?: string;
    filler_email?: string;
    additional_coaches?: string[];
    hospitality_days?: string[];
    total_amount?: number;
    amount_paid?: number;
    registration_amount?: number;
    extras_amount?: number;
    processing_fee?: number;
  };
  event: {
    id: string;
    slug?: string;
    name: string;
    start_date: string;
    end_date: string;
    venue_name?: string;
    venue_city?: string;
    venue_state?: string;
  };
}

const CoachRegistrationConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState<RegistrationData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const registrationId = searchParams.get('id');
  const eventId = searchParams.get('event_id');

  useEffect(() => {
    const fetchRegistration = async () => {
      if (!registrationId) {
        setError('No registration ID provided');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('event_coaches')
          .select(`
            id,
            coach_name,
            coach_email,
            coach_phone,
            college_name,
            division,
            payment_status,
            metadata,
            event:events (
              id,
              slug,
              name,
              start_date,
              end_date,
              venue_name,
              venue_city,
              venue_state
            )
          `)
          .eq('id', registrationId)
          .single();

        if (fetchError) {
          console.error('Error fetching registration:', fetchError);
          setError('Unable to load registration details');
          setLoading(false);
          return;
        }

        if (!data) {
          setError('Registration not found');
          setLoading(false);
          return;
        }

        // Transform the data
        const eventData = Array.isArray(data.event) ? data.event[0] : data.event;

        setRegistration({
          ...data,
          metadata: data.metadata || {},
          event: {
            id: eventData?.id || '',
            name: eventData?.name || 'Event',
            start_date: eventData?.start_date || '',
            end_date: eventData?.end_date || '',
            venue_name: eventData?.venue_name || undefined,
            venue_city: eventData?.venue_city || undefined,
            venue_state: eventData?.venue_state || undefined,
          },
        } as RegistrationData);
      } catch (err) {
        console.error('Error:', err);
        setError('An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchRegistration();
  }, [registrationId]);

  const formatAmount = (cents: number | undefined) => {
    if (!cents) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatEventDates = () => {
    if (!registration?.event.start_date) return 'TBD';
    
    const startDate = parseLocalDate(registration.event.start_date);
    const endDate = registration.event.end_date ? parseLocalDate(registration.event.end_date) : null;
    
    if (endDate) {
      return `${format(startDate, 'MMMM d')} - ${format(endDate, 'MMMM d, yyyy')}`;
    }
    return format(startDate, 'MMMM d, yyyy');
  };

  const getVenueLocation = () => {
    const { venue_name, venue_city, venue_state } = registration?.event || {};
    if (!venue_name && !venue_city && !venue_state) return 'TBD';
    return [venue_name, venue_city, venue_state].filter(Boolean).join(', ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading registration details...</p>
        </div>
      </div>
    );
  }

  if (error || !registration) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive mb-4">{error || 'Registration not found'}</p>
            <Button onClick={() => navigate('/events')}>
              Back to Events
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const additionalCoaches = registration.metadata.additional_coaches || [];
  const hospitalityDays = registration.metadata.hospitality_days || [];

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Registration Confirmed!</h1>
          <p className="text-muted-foreground">
            Thank you for registering. A confirmation email has been sent to your email address.
          </p>
        </div>

        {/* Event Details */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4 text-primary">Event Information</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{registration.event.name}</p>
                  <p className="text-sm text-muted-foreground">{formatEventDates()}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <p className="text-sm">{getVenueLocation()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Registration Details */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4 text-primary">Registration Details</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{registration.coach_name}</p>
                  <p className="text-sm text-muted-foreground">Primary Coach</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <p className="text-sm">{registration.coach_email}</p>
              </div>
              {registration.coach_phone && (
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <p className="text-sm">{registration.coach_phone}</p>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Building className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm">{registration.college_name}</p>
                  <p className="text-sm text-muted-foreground">{registration.division}</p>
                </div>
              </div>
              {additionalCoaches.length > 0 && (
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Additional Coaches:</p>
                    <ul className="text-sm text-muted-foreground">
                      {additionalCoaches.map((coach, index) => (
                        <li key={index}>{coach}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Summary */}
        {(registration.metadata.total_amount || registration.payment_status === 'paid') && (
          <Card className="mb-6 border-green-200 bg-green-50/50">
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4 text-green-700 flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payment Summary
              </h2>
              <div className="space-y-2">
                {registration.metadata.registration_amount !== undefined && (
                  <div className="flex justify-between text-sm">
                    <span>Registration Fee</span>
                    <span>{formatAmount(registration.metadata.registration_amount)}</span>
                  </div>
                )}
                {registration.metadata.extras_amount !== undefined && registration.metadata.extras_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Additional Coaches</span>
                    <span>{formatAmount(registration.metadata.extras_amount)}</span>
                  </div>
                )}
                {registration.metadata.processing_fee !== undefined && registration.metadata.processing_fee > 0 && (
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Processing Fee</span>
                    <span>{formatAmount(registration.metadata.processing_fee)}</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span>Total Paid</span>
                    <span className="text-green-700">{formatAmount(registration.metadata.amount_paid || registration.metadata.total_amount)}</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Payment Status</span>
                  <span className="text-green-600 font-medium capitalize">
                    {registration.payment_status === 'free' ? 'Free Registration' : registration.payment_status}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hospitality Days */}
        {hospitalityDays.length > 0 && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4 text-primary">Hospitality Attendance</h2>
              <p className="text-sm text-muted-foreground mb-2">You're registered for hospitality on:</p>
              <div className="flex flex-wrap gap-2">
                {hospitalityDays.map((day, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary"
                  >
                    {day}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check-In Information */}
        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4 text-primary underline">
              College Coach Check-In Information
            </h2>
            <p className="text-sm mb-4">
              College coach check-in will begin <strong>One Hour before the event starts</strong> and will take place at the following location:{' '}
              <strong>Tournament Headquarters</strong> <em>(exclusive college coach check-in location)</em>
            </p>
            <p className="text-sm text-muted-foreground">
              We appreciate your participation and look forward to welcoming you on site. 
              Should you have any questions, please do not hesitate to contact us.
            </p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <p className="text-sm">
              <strong>Sincerely,</strong><br />
              Mike Enright<br />
              Owner / Director<br />
              RecruitLook Hoops<br />
              816-223-7452 cell<br />
              <a href="mailto:Michael.Enright118@gmail.com" className="text-primary hover:underline">
                Michael.Enright118@gmail.com
              </a>
            </p>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            onClick={() => navigate('/events')}
          >
            Browse More Events
          </Button>
          {registration.event.id && (
            <Button
              onClick={() => navigate(`/events/${registration.event.slug || registration.event.id}`)}
            >
              View Event Details
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachRegistrationConfirmation;